import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { queryKeys } from '@/app/query';
import { fetchCars, type ConsumerCar } from '@/entities/car/api';
import { fetchNotifications } from '@/entities/notification/api';
import { loadOrderDraft } from '@/entities/order/draftStorage';
import { useActiveOrders } from '@/entities/order/queries';
import { Banner } from '@/shared/ui/Banner';
import { Button } from '@/shared/ui/Button';
import { Content, Screen } from '@/shared/ui/Layout';
import { Row, RowList, RowWithAction } from '@/shared/ui/RowList';
import { IconButton, TopBar } from '@/shared/ui/TopBar';
import { BellIcon, CarIcon, FuelIcon, HistoryIcon } from '@/shared/ui/icons';
import { CarBrandLogo } from '@/shared/ui/CarBrandLogo';
import { InstallBanner } from '@/features/pwa/InstallBanner';
import { PasskeyOffer } from '@/features/auth/PasskeyOffer';
import { PaymentNotice } from '@/features/order/PaymentNotice';
import { ActiveOrderCard } from './ActiveOrderCard';
import { PendingSbpPayments } from './PendingSbpPayments';
import { RecentOrders } from './RecentOrders';
import { ResumeDraftCard } from './ResumeDraftCard';
import { useDocumentTitle } from '@/shared/lib/useDocumentTitle';

/**
 * Screen C1 — the home tab.
 *
 * Three independent loads, three independent fates. The mobile clients disagree about this and
 * iOS is wrong: it runs cars, active orders and history sequentially inside one `do`, so any of
 * the three takes the entire screen down. Here each query owns its own piece of the page —
 *
 * - **active orders** decide the top of the screen, and their failure is the one failure that
 *   suppresses the CTA. «Не удалось загрузить» is never collapsed into «заказов нет»: the bug
 *   that costs the most on this screen is inviting somebody to order a second refuelling while a
 *   tanker is already driving to their first one (audit 2026-08-17);
 * - **cars** decide the subtitle of one row, and a failure there is inline text, not a screen;
 * - **the unread count** is decoration. Any failure means «no badge» and never an error state.
 *
 * Two deliberate departures from Android and iOS, both recorded in docs/27 §6:
 *
 * 1. **The CTA does not disappear when an order is running.** Both clients hide it as soon as the
 *    array is non-empty — a leftover from «one active order per account», which decision B11
 *    replaced with «one per car». An account with three cars currently cannot start a second
 *    order from the home screen at all. Here the cards get a secondary «Заказать ещё» underneath.
 * 2. **The first-car gate is soft.** Zero cars is a real state of this screen, not a wall: the
 *    CTA points at `/first-car` and says why. A failed car load is a THIRD state — never read as
 *    «машин нет», because the order wizard must ask the server rather than guess.
 */
export function HomeTab() {
  useDocumentTitle('Главная');
  const navigate = useNavigate();

  const cars = useQuery({ queryKey: queryKeys.cars, queryFn: fetchCars });
  const activeOrders = useActiveOrders();

  // One row is fetched for a number that does not depend on `limit`: `unread_count` is computed
  // over the whole history server-side, so counting the returned array would always yield 1.
  const unread = useQuery({
    queryKey: queryKeys.unreadCount,
    queryFn: () => fetchNotifications(1, 0),
    select: (page) => page.unread_count,
    retry: false,
  });

  const orders = activeOrders.data;
  const garage = cars.data;

  /**
   * The unfinished draft, read once on mount.
   *
   * Read once rather than watched: the wizard is the only thing that writes it, and it is not on
   * screen while this is. Re-reading storage on every render would also make the card flicker back
   * after it is dismissed.
   */
  const [resumableDraft] = useState(() => loadOrderDraft());

  /**
   * Straight into the wizard, or into the one step that has to happen first.
   *
   * A single car skips the picker: choosing from a list of one is a tap that asks nothing. With
   * more than one the picker is the honest first question, and it is also where «ещё одну машину»
   * comes back to.
   */
  const startOrder = () => {
    if (garage === undefined) return;
    if (garage.length === 0) {
      // `returnTo` — чтобы намерение «заказать» пережило вставленный посередине шаг: форма
      // первой машины вернёт в шлюз заказа, а не на главную. Без него человек с пустым гаражом
      // добавлял машину и начинал заказ заново, недоумевая, куда он делся.
      navigate({ to: '/first-car', search: { returnTo: 'picker' } });
      return;
    }
    navigate({ to: '/order/car' });
  };

  // One active order per car is enforced by a unique index, so a garage fully covered by running
  // orders has nothing left to order for. The server stays the authority (409
  // `active_order_exists_for_car`) — this only avoids offering a button that cannot succeed.
  const everyCarBusy =
    garage !== undefined && garage.length > 0 && (orders?.length ?? 0) >= garage.length;

  const ctaHint =
    garage !== undefined && garage.length === 0
      ? 'Добавьте технику, чтобы заказать'
      : everyCarBusy
        ? 'На всю вашу технику уже есть активные заявки'
        : null;

  return (
    <Screen>
      <HomeTopBar
        unreadCount={unread.data ?? 0}
        onOpenNotifications={() => navigate({ to: '/notifications' })}
      />

      <Content>
        <div className="flex flex-col gap-4">
          {/* Ещё выше платежей — но только когда человека привели сюда с экрана оплаты. Это
              ответ на вопрос «почему я здесь», и он должен встретить его раньше, чем он начнёт
              искать свой заказ. Ничего не случилось — блока нет. */}
          <PaymentNotice />

          {/* Самый верх: деньги по СБП уже ушли или вот-вот уйдут, а заказа ещё нет. Это
              единственное на экране, что говорит человеку, что с его оплатой, — выше и черновика,
              и заявок. Ничего не ждёт — блока нет. */}
          <PendingSbpPayments />

          {/* Above the CTA, because it IS the CTA for somebody who already started an order —
              offering «Заказать заправку» over an unfinished выезд asks them to start again. */}
          {resumableDraft !== null && <ResumeDraftCard draft={resumableDraft} />}

          {/* Asked once, and never in the way: it renders nothing at all until the device, the
              browser and the server have all said they can do it. */}
          <PasskeyOffer />

          {orders !== undefined ? (
            orders.length > 0 ? (
              <section className="flex flex-col gap-2.5">
                <h2 className="sr-only">Активные заявки</h2>

                {/* Data on screen plus a failed refresh: keep the cards, admit they may be old.
                    Replacing them with the error block would hide a выезд that is still running. */}
                {activeOrders.isError && (
                  <Banner
                    tone="warning"
                    title="Статус мог устареть"
                    action={
                      <Button
                        variant="ghost"
                        onClick={() => void activeOrders.refetch()}
                        isLoading={activeOrders.isFetching}
                      >
                        Обновить
                      </Button>
                    }
                  >
                    Не удалось получить свежие данные о заявке.
                  </Banner>
                )}

                {/* Server order is `created_at` DESC and is not re-sorted here: a second source of
                    truth for ordering buys nothing on a list this short. */}
                {orders.map((order) => (
                  <ActiveOrderCard key={order.id} order={order} />
                ))}

                <div className="mt-1.5">
                  <Button
                    variant="secondary"
                    onClick={startOrder}
                    disabled={everyCarBusy}
                    aria-describedby={ctaHint === null ? undefined : CTA_HINT_ID}
                  >
                    Заказать ещё
                  </Button>
                  <CtaHint text={ctaHint} />
                </div>
              </section>
            ) : (
              <section className="flex flex-col">
                <OrderHero />
                <Button
                  onClick={startOrder}
                  aria-describedby={ctaHint === null ? undefined : CTA_HINT_ID}
                >
                  Заказать заправку
                </Button>
                <CtaHint text={ctaHint} />
              </section>
            )
          ) : activeOrders.isError ? (
            <ActiveOrdersFailed
              onRetry={() => void activeOrders.refetch()}
              isRetrying={activeOrders.isFetching}
            />
          ) : (
            <ActiveOrdersSkeleton />
          )}

          <RowList>
            {cars.isError ? (
              // The third state. Android's full sentence ends «…Попробуйте ещё раз» — the retry
              // button next to it says that better, and the row subtitle is a single clipped line.
              <RowWithAction
                icon={
                  <span className="grid h-10 w-10 place-items-center rounded-sm bg-surface-alt text-ink">
                    <CarIcon />
                  </span>
                }
                title="Мой парк"
                subtitle="Не удалось загрузить список"
                onClick={() => navigate({ to: '/cars' })}
                action={
                  <button
                    type="button"
                    onClick={() => void cars.refetch()}
                    aria-label="Повторить загрузку парка"
                    className="shrink-0 rounded-sm px-2.5 py-2 text-[13px] font-semibold text-accent"
                  >
                    Повторить
                  </button>
                }
              />
            ) : (
              <Row
                // The first car's emblem, exactly as both mobile clients do it: the row is a
                // shortcut to the garage, and its badge is the garage's face. An empty garage
                // has no face yet, so it keeps the generic glyph.
                icon={
                  garage && garage.length > 0 && garage[0] !== undefined ? (
                    <CarBrandLogo brand={garage[0].brand} size={40} />
                  ) : (
                    <CarIcon />
                  )
                }
                title="Мой парк"
                subtitle={garage ? garageSummary(garage) : undefined}
                onClick={() => navigate({ to: '/cars' })}
              />
            )}
            <Row
              icon={<HistoryIcon />}
              title="История заказов"
              onClick={() => navigate({ to: '/history' })}
            />
          </RowList>

          {/* The one-tap repeat, under the rows rather than above them: it is a shortcut for
              somebody who fuels the same car every week, and it must not push the garage down for
              an account that has never ordered. Renders nothing when there is no history. */}
          <RecentOrders />

          {/* Last in the column, and only once the garage is not empty. «After a first successful
              action, never on load»: somebody who has added a car has set the app up for
              themselves, which is the earliest honest moment to suggest keeping it on the home
              screen. Placed above the rows it would push real content down for an advert. */}
          <InstallBanner enabled={garage !== undefined && garage.length > 0} />
        </div>
      </Content>

    </Screen>
  );
}

const CTA_HINT_ID = 'home-cta-hint';

function CtaHint({ text }: { text: string | null }) {
  if (text === null) return null;
  return (
    <p id={CTA_HINT_ID} className="mt-2.5 text-center text-[12px] leading-[1.3333] text-ink-soft">
      {text}
    </p>
  );
}

/**
 * Общая шапка, только в центре вместо слова «Главная» логотип целиком — знак и TWINTURBO
 * (решение владельца 18.09.2026). Раньше у главной была своя шапка с заголовком СЛЕВА, как в
 * прототипе, а у остальных экранов — по центру; теперь рисунок один на всё приложение, см. `TopBar`.
 * «Главная» остаётся именем заголовка для экранного диктора.
 */
function HomeTopBar({
  unreadCount,
  onOpenNotifications,
}: {
  unreadCount: number;
  onOpenNotifications: () => void;
}) {
  return (
    <TopBar
      title="Главная"
      brand
      trailing={
        <div className="relative">
          <IconButton label={bellLabel(unreadCount)} onClick={onOpenNotifications}>
            <BellIcon />
          </IconButton>
          {unreadCount > 0 && (
            // Decorative: the count is already in the button's accessible name, and a dot announced
            // separately would just add noise. `ring-bg` keeps it legible over the button's edge.
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-px -top-px h-[9px] w-[9px] rounded-full bg-accent ring-2 ring-bg"
            />
          )}
        </div>
      }
    />
  );
}

/**
 * The badge is a 9px dot in all three clients, so the number itself exists only for screen
 * readers — which is precisely the case that gains from it, since a dot conveys nothing at all.
 */
function bellLabel(unreadCount: number): string {
  if (unreadCount <= 0) return 'Уведомления';
  const one = unreadCount % 10 === 1 && unreadCount % 100 !== 11;
  return `Уведомления, ${unreadCount} ${one ? 'непрочитанное' : 'непрочитанных'}`;
}

/** «Toyota Camry · A123BC77 · ещё 2» — the garage in one line, Android's `carsSubtitle`. */
function garageSummary(cars: ConsumerCar[]): string {
  const first = cars[0];
  if (first === undefined) return 'Добавьте технику';

  const name = [first.brand, first.model]
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' ');
  // Хвост — номер, а у техники без него название вида: пустой хвост оставил бы висящее « · ».
  const tail = first.plate.trim().length > 0 ? first.plate.trim() : first.category_name.trim();
  const head = [name, tail].filter((part) => part.length > 0).join(' · ');
  const rest = cars.length - 1;

  return rest > 0 ? `${head} · ещё ${rest}` : head;
}

/** `home-empty` — the 120px circle, the promise and the subtitle, straight from the prototype. */
function OrderHero() {
  return (
    <div className="flex flex-col items-center px-2 pb-5 pt-7 text-center">
      <span
        className="mb-4 grid h-[120px] w-[120px] place-items-center rounded-full bg-accent-soft text-accent-soft-ink"
        aria-hidden="true"
      >
        <FuelIcon size={44} />
      </span>
      <h2 className="text-[19px] font-extrabold leading-[1.2632]">Закажите выездную заправку</h2>
      <p className="mt-2 max-w-[240px] text-ink-soft">
        Приедем и зальём топливо в ваш автомобиль — на парковке, дома или на работе
      </p>
    </div>
  );
}

/**
 * The state that must never be confused with «заказов нет». It replaces the hero AND the CTA:
 * while it is unknown whether a tanker is on its way, inviting a second order is the worst
 * possible guess.
 */
function ActiveOrdersFailed({
  onRetry,
  isRetrying,
}: {
  onRetry: () => void;
  isRetrying: boolean;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-3.5 rounded-md border border-line bg-surface p-4"
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      <p className="text-[13.5px] leading-[1.4]">
        Не удалось загрузить ваши заявки. Проверьте связь и попробуйте снова.
      </p>
      <Button variant="secondary" onClick={onRetry} isLoading={isRetrying}>
        Повторить
      </Button>
    </div>
  );
}

/**
 * A card-shaped placeholder rather than a centred spinner. On the web the first paint of this
 * screen also covers session restoration, and a lone spinner on an empty page reads as «ничего
 * не загрузилось»; a shape that matches what is coming reads as «сейчас будет».
 */
function ActiveOrdersSkeleton() {
  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">Загружаем ваши заявки</span>
      <div
        aria-hidden="true"
        className="h-[68px] animate-pulse rounded-md border border-line bg-surface"
        style={{ boxShadow: 'var(--shadow-card)' }}
      />
    </div>
  );
}
